import { useState } from "react";
import { toast } from "sonner";
import { PageHero } from "@/components/PageHero";
import { CONTACT_EMAILS, CONTACT_REASONS } from "@/data/content";

const EMPTY = { name: "", email: "", organisation: "", reason: "Editorial enquiry", subject: "", message: "" };

const ContactHeader = () => (
  <section data-testid="contact-page-header" className="border-b border-atlantic/10 bg-sand-dark py-16 lg:py-20">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-ocean">Contact</p>
      <h1 className="mt-4 font-serif text-4xl leading-tight tracking-tight text-atlantic sm:text-5xl">
        Contact Atlantic Bridge Institute
      </h1>
      <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
        Contact the editorial, research and partnership teams regarding reporting, interviews, corrections,
        collaboration and institutional enquiries.
      </p>
    </div>
  </section>
);

export default function ContactPage() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ "form-name": "atlantic-contact", ...form }).toString(),
      });
      if (!res.ok) throw new Error("Contact submission failed");
      toast.success("Thank you. Your message has been received.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Please check the form fields and try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border border-atlantic/20 bg-white px-4 py-3 text-sm text-atlantic placeholder:text-slate-400 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold";
  const labelCls = "text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500";

  return (
    <main data-testid="contact-page">
      <ContactHeader />
      <section className="bg-sand py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 lg:grid-cols-12 lg:gap-12 lg:px-8">
          <div className="lg:col-span-7">
            <form name="atlantic-contact" data-netlify="true" data-testid="contact-page-form" onSubmit={submit} className="border border-atlantic/10 bg-white p-7 sm:p-9">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="cp-name" className={labelCls}>Name</label>
                  <input id="cp-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <label htmlFor="cp-email" className={labelCls}>Email</label>
                  <input id="cp-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.org" className={`mt-2 ${inputCls}`} />
                </div>
              </div>
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="cp-org" className={labelCls}>Organisation</label>
                  <input id="cp-org" data-testid="contact-organisation-input" value={form.organisation} onChange={set("organisation")} placeholder="Organisation (optional)" className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <label htmlFor="cp-reason" className={labelCls}>Reason for contact</label>
                  <select id="cp-reason" data-testid="contact-reason-select" value={form.reason} onChange={set("reason")} className={`mt-2 ${inputCls}`}>
                    {CONTACT_REASONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-5">
                <label htmlFor="cp-subject" className={labelCls}>Subject</label>
                <input id="cp-subject" data-testid="contact-subject-input" required value={form.subject} onChange={set("subject")} placeholder="Subject of your message" className={`mt-2 ${inputCls}`} />
              </div>
              <div className="mt-5">
                <label htmlFor="cp-message" className={labelCls}>Message</label>
                <textarea id="cp-message" data-testid="contact-message-input" required rows={6} value={form.message} onChange={set("message")} placeholder="How can we help?" className={`mt-2 resize-y ${inputCls}`} />
              </div>
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={loading}
                className="mt-7 w-full bg-atlantic px-6 py-3.5 text-sm font-semibold tracking-wide text-white transition-colors hover:bg-atlantic-light disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send Message"}
              </button>
            </form>
          </div>
          <aside className="lg:col-span-4 lg:col-start-9">
            <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-500">Direct Desks</p>
            <div className="mt-6 space-y-4">
              {CONTACT_EMAILS.map((c) => (
                <div key={c.label} className="border border-atlantic/10 bg-white p-5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">{c.label}</p>
                  <p data-testid={`contact-page-email-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="mt-1.5 text-sm italic leading-relaxed text-slate-500">
                    {c.email ? <a href={`mailto:${c.email}`} className="font-medium text-ocean underline underline-offset-2">{c.note}</a> : c.note}
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm leading-relaxed text-slate-500">
              The general inbox has been used in earlier two-way email tests. For editorial, research or partnership enquiries,
              please use the contact form and select the relevant category. Form delivery is reviewed through Netlify.
            </p>
            <p className="mt-8 border-l-2 border-gold pl-5 text-sm leading-relaxed text-slate-600">
              Corrections are handled under our{" "}
              <a href="/editorial-standards" data-testid="contact-standards-link" className="font-semibold text-ocean underline-offset-4 hover:underline">
                Editorial Standards
              </a>
              . Every correction request is reviewed by the editorial desk.
            </p>
          </aside>
        </div>
      </section>
    </main>
  );
}
